import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ApiError, api } from "@/lib/api";
import { Navigate, useLoaderData } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { DismissWithReason } from "./dismiss-with-reason";
import { AutoDecidedFilter, LearningOutcome, type LearningOutcomeFields } from "./learning-outcome";

/**
 * `/agents/metodej/learnings`: what Hubert found in people's prompts for Metoděj.
 * Only Active lessons reach Metoděj's prompt; every decision can be undone.
 */

type Status = "draft" | "active" | "dismissed" | "retire_proposed" | "expired";

type Learning = LearningOutcomeFields & {
  id: string;
  text: string;
  evidence: string;
  evidenceCount: number;
  status: Status;
  /** Why it was dismissed: Hub's reason, or the note of whoever dismissed it. */
  resolvedNote: string | null;
  createdAt: string;
  project: { id: string; name: string } | null;
  sourceUser: { id: string; name: string | null } | null;
  reviewedBy: { id: string; name: string | null } | null;
};

const TABS = ["draft", "active", "retire_proposed", "expired", "dismissed", "all"] as const;
type Tab = (typeof TABS)[number];

/** Written on this page rather than found in prompts; the server stamps the evidence so. */
const WRITTEN_BY_HAND = "Zapsal ručně";

function AddLesson({ onAdded }: { onAdded: () => void }) {
  const { t } = useTranslation();
  const [text, setText] = useState("");
  const [projectId, setProjectId] = useState("");
  const [projects, setProjects] = useState<Array<{ id: string; name: string }>>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<{ projects: Array<{ id: string; name: string; archived: boolean }> }>(
        "/api/projects?pageSize=200",
      )
      .then((res) =>
        setProjects(
          res.projects
            .filter((p) => !p.archived)
            .sort((a, b) => a.name.localeCompare(b.name, "cs")),
        ),
      )
      .catch(() => setProjects([]));
  }, []);

  async function add() {
    setBusy(true);
    setError(null);
    try {
      await api.post("/api/coder-learnings", { text, projectId: projectId || null });
      setText("");
      setProjectId("");
      onAdded();
    } catch {
      setError(t("agentTabs.metodejLearnings.addFailed"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card data-testid="coder-learning-add">
      <CardContent className="space-y-2 pt-4">
        <p className="text-sm font-medium">{t("agentTabs.metodejLearnings.addTitle")}</p>
        <p className="text-xs text-muted-foreground">{t("agentTabs.metodejLearnings.addHint")}</p>
        <textarea
          className="w-full rounded-md border bg-background p-2 text-sm"
          rows={3}
          maxLength={1000}
          value={text}
          aria-label={t("agentTabs.metodejLearnings.newLesson")}
          onChange={(e) => setText(e.target.value)}
        />
        <div className="flex flex-wrap items-center gap-2">
          <select
            className="rounded-md border bg-background px-2 py-1 text-sm"
            value={projectId}
            aria-label={t("agentTabs.metodejLearnings.appliesTo")}
            onChange={(e) => setProjectId(e.target.value)}
          >
            <option value="">{t("agentTabs.metodejLearnings.generalLong")}</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
          <Button
            type="button"
            size="sm"
            disabled={busy || !text.trim()}
            onClick={() => void add()}
          >
            {t("agentTabs.metodejLearnings.add")}
          </Button>
          {error && <span className="text-xs text-destructive">{error}</span>}
        </div>
      </CardContent>
    </Card>
  );
}

export function CoderLearningsPage() {
  const { t } = useTranslation();
  const { user } = useLoaderData({ from: "/_app" });
  const [tab, setTab] = useState<Tab>("draft");
  const [rows, setRows] = useState<Learning[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [editing, setEditing] = useState<Record<string, string>>({});
  const [mineMessage, setMineMessage] = useState<string | null>(null);
  const [autoOnly, setAutoOnly] = useState(false);

  const load = useCallback(
    (which: Tab) => {
      setRows(null);
      const query = new URLSearchParams({
        ...(which === "all" ? {} : { status: which }),
        ...(autoOnly ? { auto: "1" } : {}),
      }).toString();
      return api
        .get<{ learnings: Learning[] }>(`/api/coder-learnings${query ? `?${query}` : ""}`)
        .then((res) => setRows(res.learnings))
        .catch(() => setError(t("agentTabs.metodejLearnings.loadFailed")));
    },
    [t, autoOnly],
  );

  useEffect(() => {
    if (user.globalRole !== "admin") return;
    void load(tab);
  }, [user.globalRole, tab, load]);

  async function save(
    id: string,
    body: { status?: Status; text?: string; note?: string | undefined },
  ) {
    setBusyId(id);
    setError(null);
    try {
      await api.patch(`/api/coder-learnings/${id}`, body);
      setEditing(({ [id]: _, ...rest }) => rest);
      await load(tab);
    } catch {
      setError(t("agentTabs.metodejLearnings.saveFailed"));
    } finally {
      setBusyId(null);
    }
  }

  async function mineNow() {
    setMineMessage(null);
    try {
      await api.post("/api/coder-learnings/mine", {});
      setMineMessage(t("agentTabs.metodejLearnings.mineStarted"));
    } catch (err) {
      setMineMessage(
        err instanceof ApiError && err.status === 503
          ? t("agentTabs.metodejLearnings.aiNotConfigured")
          : t("agentTabs.metodejLearnings.mineFailed"),
      );
    }
  }

  if (user.globalRole !== "admin") return <Navigate to="/" />;

  return (
    <div className="space-y-4 p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold">{t("agentTabs.metodejLearnings.title")}</h2>
          <p className="text-sm text-muted-foreground">{t("agentTabs.metodejLearnings.intro")}</p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <Button type="button" variant="outline" size="sm" onClick={() => void mineNow()}>
            {t("agentTabs.metodejLearnings.mineNow")}
          </Button>
          {mineMessage && <p className="text-xs text-muted-foreground">{mineMessage}</p>}
        </div>
      </div>

      <AddLesson
        onAdded={() => {
          if (tab === "active") void load("active");
          else setTab("active");
        }}
      />

      <div className="flex gap-1 border-b">
        {TABS.map((key) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            data-testid={`coder-learnings-tab-${key}`}
            className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium ${
              tab === key
                ? "border-primary text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            {t(`agentTabs.metodejLearnings.tabs.${key}`)}
          </button>
        ))}
        <AutoDecidedFilter checked={autoOnly} onChange={setAutoOnly} />
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}
      {rows && rows.length === 0 && (
        <p className="text-sm text-muted-foreground" data-testid="coder-learnings-empty">
          {t("agentTabs.metodejLearnings.empty")}
        </p>
      )}

      {rows?.map((r) => {
        const draft = editing[r.id];
        return (
          <Card key={r.id} data-testid="coder-learning-card">
            <CardContent className="space-y-2 pt-4">
              <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                <Badge variant="secondary">
                  {r.project ? r.project.name : t("agentTabs.metodejLearnings.general")}
                </Badge>
                <span>
                  {r.evidence.startsWith(WRITTEN_BY_HAND)
                    ? t("agentTabs.metodejLearnings.writtenBy", {
                        name: r.sourceUser?.name ?? t("agentTabs.metodejLearnings.someone"),
                      })
                    : t("agentTabs.metodejLearnings.seen", {
                        count: r.evidenceCount,
                        name: r.sourceUser?.name ?? t("agentTabs.metodejLearnings.someone"),
                      })}
                </span>
                {r.reviewedBy && r.status !== "draft" && (
                  <span>
                    ·{" "}
                    {t(
                      r.status === "active"
                        ? "agentTabs.metodejLearnings.activatedBy"
                        : "agentTabs.metodejLearnings.dismissedBy",
                      { name: r.reviewedBy.name },
                    )}
                  </span>
                )}
              </div>
              <LearningOutcome learning={r} />
              {r.status === "dismissed" && r.resolvedNote && (
                <p className="text-xs text-muted-foreground" data-testid="learning-resolved-note">
                  {r.reviewedBy
                    ? t("agentTabs.metodejLearnings.why")
                    : t("agentTabs.metodejLearnings.dismissedByHub")}
                  {r.resolvedNote}
                </p>
              )}
              {draft === undefined ? (
                <p className="text-sm">{r.text}</p>
              ) : (
                <textarea
                  className="w-full rounded-md border bg-background p-2 text-sm"
                  rows={3}
                  value={draft}
                  aria-label={t("agentTabs.metodejLearnings.text")}
                  onChange={(e) => setEditing((prev) => ({ ...prev, [r.id]: e.target.value }))}
                />
              )}
              <details className="text-xs text-muted-foreground">
                <summary className="cursor-pointer">
                  {t("agentTabs.metodejLearnings.whySummary")}
                </summary>
                <p className="mt-1 whitespace-pre-wrap">{r.evidence}</p>
              </details>
              <div className="flex flex-wrap gap-2">
                {draft !== undefined ? (
                  <>
                    <Button
                      type="button"
                      size="sm"
                      disabled={busyId === r.id || !draft.trim()}
                      onClick={() => void save(r.id, { text: draft })}
                    >
                      {t("agentTabs.metodejLearnings.save")}
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setEditing(({ [r.id]: _, ...rest }) => rest)}
                    >
                      {t("agentTabs.metodejLearnings.cancel")}
                    </Button>
                  </>
                ) : (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setEditing((prev) => ({ ...prev, [r.id]: r.text }))}
                  >
                    {t("agentTabs.metodejLearnings.edit")}
                  </Button>
                )}
                {r.status !== "active" && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={busyId === r.id}
                    onClick={() => void save(r.id, { status: "active" })}
                  >
                    {t("agentTabs.metodejLearnings.activate")}
                  </Button>
                )}
                {r.status !== "dismissed" && (
                  <DismissWithReason
                    busy={busyId === r.id}
                    onDismiss={(note) => void save(r.id, { status: "dismissed", note })}
                  />
                )}
                {r.status !== "draft" && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={busyId === r.id}
                    onClick={() => void save(r.id, { status: "draft" })}
                  >
                    {t("agentTabs.metodejLearnings.backToDraft")}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
